/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import {
    object, bool, string, shape, func, arrayOf, number,
} from 'prop-types';

import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import GraphqlPDPGiftMessage from '../GraphqlPDPGiftMessage';
import ConnectedGraphqlFoodProductSkus from './GraphqlFoodProductSkus';
import CommonRadioGroup from './CommonRadioGroup';
import { actions as tagManagerActions } from '../../../../../../state/ducks/TagManager';
import { clubComponentType } from '../common/types';
import { getDonationsCategory, getSSRDeviceType } from '../../../../../../state/ducks/App/App-Selectors';
import { getApplePayStatus } from '../../../../../../state/ducks/Common/Common-Selectors';
import { isVaseConfigProduct } from '../../../../../helpers/vaseConfigurations';
import VaseConfigurator from './VaseConfigurator/VaseConfigurator';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import ResponsiveImage from '../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import ProductSkusWithCallOut from './ProductSkusWithCallOut';

const styles = (theme) => ({
    root: {
        marginBottom: '20px',
        width: '100%',
        color: theme?.palette?.common?.black,
        [theme.breakpoints.down('600')]: {
            marginBottom: '10px',
        },
    },
    wrapper: {
        '& $paper': {
            boxShadow: 'none',
        },
        '& $headerContainer': {
            background: 'none',
            padding: '0px 0px 10px',
            width: 'calc(100% - 32px)',
            borderRadius: '0px',
            margin: '18px auto 0px',
            height: 'auto',
            [theme.breakpoints.up('600')]: {
                width: 'calc(100% - 48px)',
                margin: '26px auto 0px',
            },
        },
        '& $paperHeader': {
            background: 'none',
            fontSize: '20px',
            lineHeight: '26px',
            letterSpacing: '0.5px',
            fontFamily: 'Area Variable, LatoBold',
            fontWeight: '700',
            [theme.breakpoints.up('600')]: {
                fontSize: '24px',
                lineHeight: '30px',
            },
        },
        '& $radio': {
            color: '#000',
            padding: '0px 16px 0px 8px',
            '&$checked': {
                color: '#000',
            },
            '& svg': {
                fontSize: '20px',
            },
        },
        '& $skuLabelName': {
            fontSize: '16px',
            lineHeight: '24px',
        },
        '& $paperBody': {
            padding: '10px 16px',
            [theme.breakpoints.up('600')]: {
                padding: '10px 30px',
            },
        },
    },
    paper: {

    },
    paperHeader: {
        boxSizing: 'border-box',
        width: '100%',
        padding: '0 20px 0 0',
        fontWeight: '600',
        fontSize: '13px',
        lineHeight: '19px',
        background: theme?.palette?.colorNeutral30,
        borderRadius: '4px 4px 0 0',
        color: theme?.palette?.common?.black,
    },
    paperBody: {
        boxSizing: 'border-box',
        padding: '10px 20px',
    },
    radio: {
        color: theme.palette.cms?.primary || '#693388',
        padding: '6px 11px',
        '&$checked': {
            color: theme.palette.cms?.primary || '#693388',
        },
        [theme.breakpoints.down('600')]: {
            '& svg': {
                fontSize: '24px',
            },
        },
    },
    checked: {
        color: theme.palette.cms?.primary || '#693388',
        '&$checked': {
            color: theme.palette.cms?.primary || '#693388',
        },
    },
    skuLabelName: {
        color: '#000',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
        },
    },
    skuLabelAmount: {
        color: '#585858',
        marginLeft: '10px',
        fontWeight: '500',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
            textAlign: 'right',
        },
        '&--retail': {
            marginLeft: '5px',
            textDecoration: 'line-through',
            color: '#ed0000',
        },
        '&--sale': {
            marginLeft: '5px',
            color: '#7DC383',
        },
    },
    formLabel: {
        display: 'flex',
        width: '100%',
        '& >div': {
            width: '100%',
            justifyContent: 'space-between',
            display: 'flex',
        },
    },
    optionalMessageMobile: {
        // marginTop: 10,
        padding: '0 15px',
        background: '#f4f2f7',
        '& >div>div': {
            padding: '5px 0px 20px 0px',
        },
        '& .MuiInput-underline': {
            '&:before': {
                borderBottom: 'none',
            },
            '&:after': {
                borderBottom: 'none',
            },
        },
        '& .MuiPaper-elevation1': {
            boxShadow: 'none',
        },
    },
    upSellMessgaeRadioButton: {
        background: theme?.palette?.colorNeutral30,
    },
    CircularProgress: {
        textAlign: 'center',
        margin: '0 auto',
    },
    sectionDivider: {
        border: `1px solid ${theme.palette.cms?.flowerPdpButton || '#65388b'}`,
        background: theme.palette.cms?.flowerPdpButton || '#65388b',
        width: '100%',
        margin: '15px auto',
        borderRadius: '2px',
    },
    customizeRadioContainer: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        justifyContent: 'center',
        margin: '10px 5px',
    },
    hideRadio: {
        display: 'none !important',
    },
    customSelectedVariant: {
        marginLeft: 'initial',
        border: `1px solid ${theme.palette.borderGrayColor || '#ededed'}`,
        padding: '5px',
        marginRight: '8px',
        flexBasis: '40%',
        borderRadius: '4px',
        '&:last-child': {
            marginRight: 0,
        },
    },
    selectedSkuProduct: {
        border: `2px solid ${theme.palette.cms?.flowerPdpButton || '#65388b'}`,
        position: 'relative',
        borderRadius: '4px',
    },
    brandDividerColor: {
        border: `1px solid ${theme.palette.cms?.flowerPdpButton || '#45b071'}`,
        background: theme.palette.cms?.flowerPdpButton || '#45b071',
    },
    brandColorBorder: {
        border: `2px solid ${theme.palette.cms?.flowerPdpButton || '#45b071'}`,
    },
    headerContainer: {
        height: '40px',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        background: theme?.palette?.colorNeutral30,
        borderRadius: '4px',
        '& img': {
            width: '100%',
            display: 'block',
            paddingLeft: '20px',
        },
    },
    labelContainer: {
        marginRight: '0px',
        paddingBottom: '12px',
    },
    pdpShortStyle: {
        '& $paperBody': {
            padding: '24px 16px 8px',
            [theme.breakpoints.up('600')]: {
                padding: '18px 30px 8px',
            },
        },
    },
    plaRedesignStyle: {
        marginBottom: '24px',
        '& $paperBody': {
            padding: '0px',
        },
        '& $labelContainer': {
            '@media screen and (min-width:600px)': {
                paddingBottom: '12px',
            },
            '&:last-child': {
                paddingBottom: '0px',
            },
        },
        '& $radio': {
            padding: '0px 5px',
        },
    },
});

const GraphqlProductSkus = ({
    classes,
    skuData,
    skuConfig,
    handleSkuChange,
    selectedSku,
    trackEvent,
    product,
    productUrl,
    location,
    brand,
    skuMonths,
    changeClubMonth,
    changeClubLength,
    clubUpsells,
    changeClubUpsell,
    selectedClubUpsells,
    baseProductSku,
    isPassportBundleProduct,
    clubProduct,
    isPdpYotpoEnabled,
    ssrDeviceType,
    setVaseConfiguratorSku,
    movieSelected,
    isStandAloneMovieSku,
    hasWine,
    floralPLARedesign,
    isPassportBannerEnabled,
    PLARedesignMobileOnly,
    isByoProduct,
    handleSkuClick,
    isCyoProduct,
    isPdpRedesignEnabled,
    isPdpRedesignShortEnabled,
    isPdpRedesignSkuEnabled,
    isPlaRedesignEnabled,
    hideUpsells,
    hidePrice,
}) => {
    const { featureFlags = {}, presentation_family: presentationFamily = '' } = useContext(CrossMerch);
    if (presentationFamily === 'food') {
        return (
            <ConnectedGraphqlFoodProductSkus
                skuData={skuData}
                clubProduct={clubProduct}
                handleSkuChange={handleSkuChange}
                trackEvent={trackEvent}
                selectedSku={selectedSku}
                product={product}
                productUrl={productUrl}
                presentation_family={presentationFamily}
                location={location}
                brand={brand}
                skuMonths={skuMonths}
                changeClubMonth={changeClubMonth}
                changeClubLength={changeClubLength}
                clubUpsells={clubUpsells}
                selectedClubUpsells={selectedClubUpsells}
                changeClubUpsell={changeClubUpsell}
                baseProductSku={baseProductSku}
                showSkuOnMobile={skuConfig?.show_sku_on_mobile}
                isPassportBundleProduct={isPassportBundleProduct || {}}
                isPdpYotpoEnabled={isPdpYotpoEnabled}
                isByoProduct={isByoProduct}
                isCyoProduct={isCyoProduct}
                isPdpRedesignEnabled={isPdpRedesignEnabled}
                isPdpRedesignSkuEnabled={isPdpRedesignSkuEnabled}
                isPlaRedesignEnabled={isPlaRedesignEnabled}
                hideUpsells={hideUpsells}
                hidePrice={hidePrice}
            />
        );
    }

    if (featureFlags['is-pdp-new-layout-enabled'] && brand.code === 'FL') {
        return (
            <ProductSkusWithCallOut
                skuData={skuData}
                trackEvent={trackEvent}
                selectedSku={selectedSku}
                handleSkuClick={handleSkuClick}
            />
        );
    }

    // TODO bladerunner: temporary solution, will change when I can confirm other logic is sound
    const isVaseConfiguratorProduct = featureFlags['is-vase-configurator-enabled'] && isVaseConfigProduct(product.partNumber);
    if (isVaseConfiguratorProduct) {
        return (
            <VaseConfigurator
                product={product}
                setVaseConfiguratorSku={setVaseConfiguratorSku}
            />
        );
    }

    const donationsCategoryName = useSelector(getDonationsCategory);
    const showMobilePassportImage = presentationFamily === 'flower' && isPassportBannerEnabled && donationsCategoryName?.toLowerCase() !== 'donations' && ssrDeviceType === 'mobile';
    let pdpReskinStyle = '';
    if (isPlaRedesignEnabled) {
        pdpReskinStyle = `${classes.wrapper} ${classes.plaRedesignStyle}`;
    } else if (isPdpRedesignShortEnabled) {
        pdpReskinStyle = `${classes.wrapper} ${classes.pdpShortStyle}`;
    }   else if (isPdpRedesignEnabled) {
        pdpReskinStyle = classes.wrapper;
    }
    return (
        <>
            {PLARedesignMobileOnly
                ? (
                    <>
                        <CommonRadioGroup
                            classes={classes}
                            skuData={skuData}
                            handleSkuChange={handleSkuChange}
                            trackEvent={trackEvent}
                            selectedSku={selectedSku}
                            presentation_family={presentationFamily}
                            product={product}
                            isPassportBundleProduct={isPassportBundleProduct}
                            floralPLARedesign={floralPLARedesign}
                            ssrDeviceType={ssrDeviceType}
                            PLARedesignMobileOnly={PLARedesignMobileOnly}
                            isPdpRedesignEnabled={isPdpRedesignEnabled}

                        />
                        {ssrDeviceType === 'desktop' && <div className={`${classes.sectionDivider} ${brand?.code === 'FB' ? classes.brandDividerColor : ''}`} />}
                    </>
                ) : (
                    <div className={`${classes.root} ${pdpReskinStyle}`}>
                        <Paper elevation={1} className={classes.paper}>
                            {isPdpRedesignShortEnabled || isPlaRedesignEnabled ? '' : (
                                <div className={`${classes.headerContainer}`}>
                                    <div className={classes.paperHeader}>
                                        {!isPdpRedesignEnabled && '1.'} Pick Your Item
                                    </div>
                                    {showMobilePassportImage && !isPdpRedesignEnabled
                                    && (
                                        <ResponsiveImage
                                            className={classes.passportEligibleImage}
                                            path="https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/blt8d4549d3cac15860/61e09d4f2e109d6c649d4aa4/PP_EligibleIcon.svg"
                                            alt="Passport Eligible"
                                            fullWidth
                                        />
                                    )}
                                </div>
                            )}
                            <div className={classes.paperBody}>
                                <Grid
                                    container
                                    direction="column"
                                    justify="flex-start"
                                >
                                    <CommonRadioGroup
                                        classes={classes}
                                        skuData={skuData}
                                        handleSkuChange={handleSkuChange}
                                        trackEvent={trackEvent}
                                        selectedSku={selectedSku}
                                        presentation_family={presentationFamily}
                                        product={product}
                                        isPassportBundleProduct={isPassportBundleProduct}
                                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                                        isPdpRedesignSkuEnabled={isPdpRedesignSkuEnabled}
                                        isPlaRedesignEnabled={isPlaRedesignEnabled}
                                    />
                                    {ssrDeviceType !== 'mobile'
                                        && (
                                            <GraphqlPDPGiftMessage
                                                skuConfig={skuConfig}
                                                selectedSku={selectedSku}
                                                movieSelected={movieSelected}
                                                isStandAloneMovieSku={isStandAloneMovieSku}
                                                hasWine={hasWine}
                                            />
                                        )}
                                </Grid>
                            </div>
                        </Paper>
                        {ssrDeviceType === 'mobile' && (
                            <GraphqlPDPGiftMessage
                                skuConfig={skuConfig}
                                selectedSku={selectedSku}
                                movieSelected={movieSelected}
                                isStandAloneMovieSku={isStandAloneMovieSku}
                                hasWine={hasWine}
                            />
                        )}
                    </div>
                )}
        </>
    );
};

const mapStateToProps = (state) => ({
    applePayStatus: getApplePayStatus(state),
    ssrDeviceType: getSSRDeviceType(state),
});

const mapDispatchToProps = {
    trackEvent: tagManagerActions.trackEvent,
};

GraphqlProductSkus.defaultProps = {
    movieSelected: null,
    isStandAloneMovieSku: false,
    skuMonths: {},
    clubUpsells: [],
    brand: {},
    changeClubMonth: () => { },
    changeClubLength: () => { },
    selectedClubUpsells: null,
    changeClubUpsell: () => { },
    baseProductSku: null,
    isPassportBundleProduct: {},
    clubProduct: false,
    isPdpYotpoEnabled: false,
    setVaseConfiguratorSku: () => { },
    floralPLARedesign: '',
    PLARedesignMobileOnly: false,
    isByoProduct: false,
    handleSkuClick: () => { },
    isCyoProduct: false,
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
    isPdpRedesignSkuEnabled: false,
    isPlaRedesignEnabled: false,
    hideUpsells: false,
    hidePrice: false,
};

GraphqlProductSkus.propTypes = {
    setVaseConfiguratorSku: func,
    classes: object.isRequired,
    hasWine: bool.isRequired,
    movieSelected: object,
    isStandAloneMovieSku: bool,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ).isRequired,
    skuConfig: shape({
        show_optional_gift_message: bool.isRequired,
        show_sku_on_mobile: bool,
    }).isRequired,
    handleSkuChange: func.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    trackEvent: func.isRequired,
    product: object.isRequired,
    location: object.isRequired,
    brand: shape({
        domain: string,
    }),
    productUrl: string.isRequired,
    skuMonths: object, // dynamic month named keys
    changeClubMonth: func,
    changeClubLength: func,
    clubUpsells: clubComponentType,
    selectedClubUpsells: string,
    changeClubUpsell: func,
    baseProductSku: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ),
    isPassportBundleProduct: shape({
        prices: shape({
            value: number,
        }),
        enable: bool,
    }),
    clubProduct: bool,
    isPdpYotpoEnabled: bool,
    ssrDeviceType: string.isRequired,
    floralPLARedesign: string,
    isPassportBannerEnabled: bool.isRequired,
    PLARedesignMobileOnly: bool,
    isByoProduct: bool,
    handleSkuClick: func,
    isCyoProduct: bool,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
    isPdpRedesignSkuEnabled: bool,
    isPlaRedesignEnabled: bool,
    hideUpsells: bool,
    hidePrice: bool,
};

const enhance = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withStyles(styles),
);

export default enhance(GraphqlProductSkus);
